import React, { useState, useRef } from 'react';
import { Button, Container, Typography, Box, TextField, Paper, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import CodeIcon from '@mui/icons-material/Code';

const FormatButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(1),
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

function WhatsAppTextFormatter() {
  const [inputText, setInputText] = useState('');
  const [formattedText, setFormattedText] = useState('');
  const textFieldRef = useRef(null);

  const formatOptions = [
    { icon: <FormatBoldIcon />, syntax: '*', tooltip: 'Bold' },
    { icon: <FormatItalicIcon />, syntax: '_', tooltip: 'Italic' },
    { icon: <StrikethroughSIcon />, syntax: '~', tooltip: 'Strikethrough' },
    { icon: <CodeIcon />, syntax: '```', tooltip: 'Monospace' },
  ];

  const applyFormatting = (syntax) => {
    const textField = textFieldRef.current;
    const startPos = textField.selectionStart;
    const endPos = textField.selectionEnd;
    const selectedText = inputText.substring(startPos, endPos);
    const newText = inputText.substring(0, startPos) + syntax + selectedText + syntax + inputText.substring(endPos);

    setInputText(newText);
    textField.focus();
  };

  const formatText = () => {
    let formatted = inputText
      .replace(/\*(.*?)\*/g, '<b>$1</b>')
      .replace(/_(.*?)_/g, '<i>$1</i>')
      .replace(/~(.*?)~/g, '<s>$1</s>')
      .replace(/```(.*?)```/g, '<code>$1</code>');

    setFormattedText(formatted);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, px: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '1600px' }}>
      <Typography variant="h4" gutterBottom align="left" sx={{ fontWeight: 'bold', mb: 1 }}>
        WhatsApp Text Formatter
      </Typography>
      <Typography sx={{ mb: 2 }}>
        This WhatsApp Text Formatter is designed to help you easily format your messages for WhatsApp. Type or paste your text, apply the formatting you want and copy it to use in your WhatsApp conversations. The tool supports bold, italic, strikethrough and monospace formatting.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, height: '100%', borderRadius: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Input Text
            </Typography>
            <Box sx={{ mb: 2 }}>
              {formatOptions.map((option, index) => (
                <FormatButton
                  key={index}
                  variant="contained"
                  onClick={() => applyFormatting(option.syntax)}
                  title={option.tooltip}
                >
                  {option.icon}
                </FormatButton>
              ))}
            </Box>
            <TextField
              inputRef={textFieldRef}
              multiline
              rows={10}
              fullWidth
              variant="outlined"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              InputProps={{
                sx: {
                  backgroundColor: '#ffffff',
                },
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={formatText}
              disabled={!inputText.trim()}
              sx={{
                mt: 2,
              }}
            >
              Format Text
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, height: '100%', borderRadius: 2, backgroundColor: '#f5f5f5' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" >
                Formatted Preview
              </Typography>
            </Box>
            <Box
              dangerouslySetInnerHTML={{ __html: formattedText }}
              sx={{
                minHeight: '300px',
                borderRadius: '4px',
                p: 2,
                overflowY: 'auto',
                backgroundColor: 'white',
              }}
            />
          </Paper>
        </Grid>
      </Grid>
      <Paper elevation={3} sx={{ p: 2, mt: 3, backgroundColor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>
          Formatting Guide
        </Typography>
        <Grid container spacing={2}>
          {[
            { icon: <FormatBoldIcon />, title: 'Bold', description: 'Emphasizes important words or phrases' },
            { icon: <FormatItalicIcon />, title: 'Italic', description: 'Adds emphasis or indicates titles' },
            { icon: <StrikethroughSIcon />, title: 'Strikethrough', description: 'Shows text that is no longer relevant or has been revised' },
            { icon: <CodeIcon />, title: 'Monospace', description: 'Displays text in fixed-width font, useful for code or technical terms' },
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box display="flex" alignItems="center">
                <Box sx={{ mr: 1, color: 'primary.main' }}>{item.icon}</Box>
                <Box>
                  <Typography variant="subtitle1">{item.title}</Typography>
                  <Typography variant="body2">{item.description}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
}

export default WhatsAppTextFormatter;